import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { api } from '@/services/api';
import { registerSchema, formatZodErrors } from '@/shared/validation';
import { FormError } from '@/shared/FormError';
import { useToast } from '@/shared/toast';

interface PublicTenant { id: number; name: string }

export function RegisterPage(): React.JSX.Element {
  const navigate = useNavigate();
  const toast = useToast();
  const [tenants, setTenants] = useState<PublicTenant[]>([]);
  useEffect(() => {
    api.get<PublicTenant[]>('/tenants/public')
      .then(res => setTenants(res.data))
      .catch(() => setTenants([]));
  }, []);

  const [form, setForm] = useState({
    email: '', password: '', nickname: '', tenantId: 1, gdprConsent: false,
  });
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null); setMessage(null); setFieldErrors({});

    const parsed = registerSchema.safeParse(form);
    if (!parsed.success) {
      const errs = formatZodErrors(parsed);
      setFieldErrors(errs);
      toast.show('Compila correttamente i campi evidenziati', 'error');
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/auth/register', parsed.data);
      const msg = 'Registrazione inviata! Controlla la tua email (anche lo spam) e clicca il link di conferma per completare la richiesta.';
      setMessage(msg);
      toast.show(msg, 'success');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      setTimeout(() => navigate('/login'), 5000);
    } catch (err: unknown) {
      const apiErr = (err as { response?: { data?: { error?: string } } })?.response?.data?.error
        ?? 'Errore registrazione. Riprova tra qualche istante.';
      setError(apiErr);
      toast.show(apiErr, 'error');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="login-shell">
      <div className="login-container">
        <div className="login-logo">
          <img src="/icon-128.webp" alt="VVF"
            style={{ width: 72, height: 72, margin: '0 auto 10px', display: 'block' }} />
          <h1>VVF Tracker</h1>
          <p>Crea un nuovo account</p>
        </div>

        <div className="login-form-wrap">
          <h2>Registrazione</h2>
          {error && <div className="error-message">{error}</div>}
          {message && (
            <div className="success-message">
              {message}
              <div style={{ marginTop: 6, fontSize: 12, opacity: 0.85 }}>
                Verrai reindirizzato al login...
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} noValidate>
            <div className="form-group">
              <label htmlFor="reg-email">Email</label>
              <input id="reg-email" type="email" value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })} />
              <FormError message={fieldErrors.email} />
            </div>
            <div className="form-group">
              <label htmlFor="reg-pass">Password</label>
              <input id="reg-pass" type="password" value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })} />
              <small style={{ color: '#666', fontSize: 12 }}>Min 8 caratteri, 1 maiuscola, 1 numero</small>
              <FormError message={fieldErrors.password} />
            </div>
            <div className="form-group">
              <label htmlFor="reg-nick">Nickname</label>
              <input id="reg-nick" type="text" value={form.nickname}
                onChange={(e) => setForm({ ...form, nickname: e.target.value })} />
              <FormError message={fieldErrors.nickname} />
            </div>
            <div className="form-group">
              <label htmlFor="reg-tenant">Distaccamento</label>
              <select id="reg-tenant" value={form.tenantId}
                onChange={(e) => setForm({ ...form, tenantId: parseInt(e.target.value, 10) })}>
                {tenants.length === 0 && <option value={1}>Caricamento...</option>}
                {tenants.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
              <FormError message={fieldErrors.tenantId} />
            </div>
            <div className="gdpr-box">
              <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                <input id="gdpr" type="checkbox" checked={form.gdprConsent}
                  onChange={(e) => setForm({ ...form, gdprConsent: e.target.checked })}
                  style={{ marginTop: 3, width: 18, height: 18, cursor: 'pointer', flexShrink: 0 }} />
                <label htmlFor="gdpr" style={{ cursor: 'pointer', fontSize: 14, lineHeight: 1.4, color: '#222' }}>
                  <strong>Accetto il trattamento dei dati personali (GDPR)</strong>
                  <div style={{ marginTop: 4, fontSize: 12, color: '#555' }}>
                    Conferisco il consenso al trattamento dei dati personali per la creazione
                    dell'account e l'erogazione del servizio, ai sensi del Regolamento UE 2016/679 (GDPR).
                    I dati saranno usati solo per il funzionamento dell'applicazione di tracciamento
                    interventi VVF e non condivisi con terzi.
                  </div>
                </label>
              </div>
            </div>
            <FormError message={fieldErrors.gdprConsent} />
            <button type="submit" className="submit-btn" disabled={submitting || !!message}>
              {submitting ? 'Invio in corso...' : message ? 'Registrato' : 'Registrati'}
            </button>
          </form>

          <div className="form-footer">
            <Link to="/login">Hai gia un account? Accedi</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
